import React, { useState, useEffect, useRef, useContext } from "react";
import { createRoot, Root } from "react-dom/client";
import mapboxgl from "mapbox-gl";
import type { ExpressionSpecification } from "mapbox-gl";
import { useMap } from "../contexts/MapContext";
import { Box, Typography, Button } from "@mui/material";
import { CheckCircle, Cancel, Undo } from "@mui/icons-material";
import MapFeaturePickerMenu, { MapFeaturePickerOption } from "./MapFeaturePickerMenu";
import { useProject } from "../api/project";
import { Layer } from "../types/layer";
import {
  DrawingContext,
  DrawingFeatureType,
  DrawingMode,
} from "../contexts/DrawingContext";
import { useSelection } from "../contexts/SelectionContext";
import { useParams } from "react-router-dom";
import TreatmentPopover from "./TreatmentPopover";
import AssetPopover from "./AssetPopover";
import NotePopover from "./NotePopover";
import { useMapboxGeocoder } from "../hooks/useMapboxGeocoder";
import { Treatment } from "../types/treatment";
import { TreatmentData } from "./AddEditTreatmentModal";
import { AssetFormData } from "./AddEditAssetModal";
import { useDeleteTreatment } from "../api/treatments";
import { useDeleteAsset } from "../api/assets";
import { asIdArray } from "../api/constructionTypes";
import { useDeleteNote } from "../api/notes";
import { useUnitSystem } from "../hooks/useUnitSystem";
import { formatFirelineIntensity, formatDistance, formatFuelLoad, formatLength, formatNumber, formatSpeed } from "../utils/units";
import AddEditTreatmentModal from "./AddEditTreatmentModal";
import AddEditAssetModal from "./AddEditAssetModal";
import VegetationMultiEditModal, {
  type RevegFeatureInfo,
} from "./VegetationMultiEditModal";
import DeleteTreatmentDialog from "./DeleteTreatmentDialog";
import type * as GeoJSON from "geojson";
import bbox from "@turf/bbox";
import { featureCollection } from "@turf/helpers";
import { bboxFromBoundary } from "../utils/boundaryUtils";
import { ProjectBoundary } from "../types/project";
import CompassButton from "./CompassButton";
import { MAP_STATUS_BAR_HEIGHT } from "./layer_components/legendSlots";
import { mapEventScreenPoint, reportLayoutSizeToMapbox } from "../utils/uiZoom";

function buildMaskGeoJSON(boundary: ProjectBoundary | null | undefined): GeoJSON.Feature {
  const worldRing: GeoJSON.Position[] = [[-180, -90], [-180, 90], [180, 90], [180, -90], [-180, -90]];
  if (!boundary) {
    return { type: "Feature", properties: {}, geometry: { type: "MultiPolygon", coordinates: [] } };
  }
  // Extract all rings from the boundary (Polygon or MultiPolygon) as holes in the world mask
  const rings: GeoJSON.Position[][] =
    boundary.type === "Polygon"
      ? (boundary.coordinates as GeoJSON.Position[][])
      : (boundary.coordinates as GeoJSON.Position[][][]).flatMap((poly) => poly);
  return {
    type: "Feature",
    properties: {},
    geometry: { type: "Polygon", coordinates: [worldRing, ...rings] },
  };
}

mapboxgl.accessToken =
  "pk.eyJ1IjoiYnVwcG9vODUiLCJhIjoiY2xzaGV6M2d6MjV4ajJudDAwOXo1bjMwcSJ9.0LeBUIZbR4UWWloslEZjlA";

// The vegetation tiles are built from seam-dissolved polygons: one rendered
// polygon can be the union of several Vegetation rows, and its "ids" property
// carries every member PK (comma-joined) while "id" is just the first member.
// Editing must target all members, or seam halves drift apart. `__id` is a
// tile-generation index, never a PK — it must not be used to address rows.
const parseVegMemberIds = (props: any): number[] => {
  if (typeof props?.ids === "string" && props.ids.length > 0) {
    const parsed = props.ids
      .split(",")
      .map((s: string) => Number(s))
      .filter((n: number) => Number.isFinite(n));
    if (parsed.length > 0) return parsed;
  }
  const single = props?.id == null ? NaN : Number(props.id);
  return Number.isFinite(single) ? [single] : [];
};

/**
 * One row of the "which did you mean?" menu that opens when a click lands on
 * features of several layers at once (an ignition under the Risk From
 * overlay, a treatment under an ignition, stacked treatments): what the row
 * shows, and what choosing it does (GH #432).
 */
interface MapPick extends MapFeaturePickerOption {
  run: () => void;
}

/** A feature's own name for the picker, falling back to its hover label. */
const featureLabel = (feature: mapboxgl.MapboxGeoJSONFeature): string | undefined => {
  const props: Record<string, unknown> = feature.properties ?? {};
  for (const key of ["name", "Name", "title", "hoverLabel"]) {
    const v = props[key];
    if (typeof v === "string" && v.trim()) return v;
  }
  return undefined;
};

/** Treatments, assets and notes are selected through the legacy query (they
 *  declare no interactive ids); this says which of the three a feature is. */
const legacyFeatureKind = (
  feature: mapboxgl.MapboxGeoJSONFeature
): "treatment" | "asset" | "note" | null => {
  if (feature.source === "treatments") return "treatment";
  if (isAssetSource(feature.source)) return "asset";
  if (feature.layer?.id === "notes") return "note";
  return null;
};

// The urban building-state overlay (UrbanBuildingStateLayer) is a second
// vector source over the same asset tiles, so a burning / burnt building
// stays clickable — and opens the same asset popover — when the Assets
// layer itself is switched off.
const URBAN_BUILDING_LAYER_ID = "urban-building-state";
const isAssetSource = (source: string | undefined): boolean =>
  source === "assets" || source === URBAN_BUILDING_LAYER_ID;

// Polygons written into the vegetation tiles by a revegetation treatment have
// no Vegetation row behind them (the tiles flag them with "reveg" and carry no
// "id"), so they can be inspected but not edited from the map.
const isRevegVegFeature = (props: any): boolean => {
  const v = props?.reveg;
  return (
    v === true ||
    v === 1 ||
    v === "1" ||
    (typeof v === "string" && v.toLowerCase() === "true")
  );
};

interface MapProps {
  layers: Layer[];
  searchable?: boolean;
  latlng?: { lat: number; lng: number };
  initialZoom?: number;
  style?: React.CSSProperties;
  /**
   * Frame the active project layers once on first load (default). The
   * listing's split view passes false and drives the camera itself, so the
   * two fits don't race.
   */
  autoFit?: boolean;
}

const Map: React.FC<MapProps> = ({
  layers,
  searchable,
  latlng,
  initialZoom,
  style,
  autoFit = true,
}) => {
  const {
    drawingFeatureType,
    coords,
    setCoords,
    drawingMode,
    setDrawingMode,
    setDrawingFeatureType,
  } = useContext(DrawingContext);
  const { setSelectedPolygonId, setSelectedLatLng } = useSelection();
  const [hoverText, setHoverText] = useState("");
  // Mirrored into the map context so the project page's footer, which sits
  // over this map's own bar, can show it. Kept local too: some pages mount
  // the map without a provider.
  const { setHoverText: publishHoverText } = useMap();
  useEffect(() => {
    publishHoverText(hoverText);
    return () => publishHoverText("");
  }, [hoverText, publishHoverText]);
  const [mapReady, setMapReady] = useState(false);
  const { project, isLoading: projectLoading } = useProject();
  const unitSystem = useUnitSystem();
  const { setMap } = useMap();

  const measureDistanceLabel = (() => {
    if (drawingMode !== DrawingMode.MEASURE) return null;
    if (!Array.isArray(coords) || coords.length < 2) return "Click on the map to add points";

    let meters = 0;
    for (let i = 1; i < coords.length; i += 1) {
      const a = coords[i - 1];
      const b = coords[i];
      if (!Array.isArray(a) || !Array.isArray(b) || a.length !== 2 || b.length !== 2) continue;
      const [lng1, lat1] = a;
      const [lng2, lat2] = b;
      if (typeof lng1 !== "number" || typeof lat1 !== "number" || typeof lng2 !== "number" || typeof lat2 !== "number") {
        continue;
      }
      meters += new mapboxgl.LngLat(lng1, lat1).distanceTo(new mapboxgl.LngLat(lng2, lat2));
    }

    if (meters >= 1000) {
      return `Distance: ${formatDistance(meters / 1000, unitSystem)}`;
    }
    return `Distance: ${formatLength(meters, unitSystem)}`;
  })();

  const handleMeasureUndo = () => {
    if (drawingMode !== DrawingMode.MEASURE) return;
    if (!Array.isArray(coords)) return;
    setCoords((coords as any[]).slice(0, Math.max(0, (coords as any[]).length - 1)) as any);
  };

  const handleMeasureClear = () => {
    if (drawingMode !== DrawingMode.MEASURE) return;
    setCoords([] as any);
  };
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [zoom] = useState(initialZoom || 12);
  const { projectId } = useParams();
  const projectIdNumber = Number(projectId) || 0;
  const { mutate: deleteTreatment } = useDeleteTreatment(projectIdNumber);
  const { mutate: deleteAsset } = useDeleteAsset(projectId);
  const { mutate: deleteNote } = useDeleteNote(projectId || "");

  // State for treatment popover
  const [selectedTreatment, setSelectedTreatment] = useState<Treatment | null>(
    null
  );
  const [selectedAsset, setSelectedAsset] = useState<GeoJSON.Feature | null>(
    null
  );
  const [selectedNote, setSelectedNote] = useState<any | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [featurePicker, setFeaturePicker] = useState<{
    anchor: { top: number; left: number };
    options: MapPick[];
  } | null>(null);
  const [treatmentToEdit, setTreatmentToEdit] = useState<TreatmentData | null>(
    null
  );
  const [assetToEdit, setAssetToEdit] = useState<AssetFormData | null>(null);
  const [selectedAssetFeatures, setSelectedAssetFeatures] = useState<Array<{ id: string | number; feature: GeoJSON.Feature }>>([]);
  const [multiEditData, setMultiEditData] = useState<{ ids: (string | number)[]; commonData: Partial<AssetFormData> } | null>(null);
  const prevHighlightedIdsRef = useRef<Set<string | number>>(new Set());
  // Shift-multi-select for vegetation polygons (mirrors the assets flow above).
  const [selectedVegetationFeatures, setSelectedVegetationFeatures] = useState<Array<{ id: string | number; feature: GeoJSON.Feature }>>([]);
  const [vegMultiEditIds, setVegMultiEditIds] = useState<number[] | null>(null);
  // A clicked revegetation-treatment polygon has no Vegetation row to edit;
  // the modal shows its details read-only instead.
  const [vegRevegInfo, setVegRevegInfo] = useState<RevegFeatureInfo | null>(null);
  // True when vegMultiEditIds came from a plain click on one polygon (whose
  // seam-dissolve members share values), so the modal keeps single-edit UX.
  const [vegEditSinglePolygon, setVegEditSinglePolygon] = useState(false);
  // Mirror of the selection count for the click handler, whose closure over
  // `selectedVegetationFeatures` is stale (the listener isn't re-attached on
  // selection changes). Lets a plain click tell "dismiss selection" from "edit".
  const selectedVegCountRef = useRef(0);
  const isShiftHeldRef = useRef(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [treatmentToDelete, setTreatmentToDelete] = useState<
    { id: number; seriesSize: number } | null
  >(null);

  const { geocoderContainerRef } = useMapboxGeocoder(map, mapReady);

  // Ensure we only auto-fit bounds once (initial load)
  const hasFitBounds = useRef(false);

  // Marker for confirm/cancel at last click location
  const confirmMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const confirmMarkerRootRef = useRef<Root | null>(null);

  const getLastLngLat = (): [number, number] | null => {
    if (!coords) return null;
    // Point: coords is [lng, lat]
    if (
      Array.isArray(coords) &&
      coords.length === 2 &&
      typeof coords[0] === "number"
    ) {
      return coords as [number, number];
    }
    // Line/Polygon: coords is [[lng, lat], ...]
    if (
      Array.isArray(coords) &&
      coords.length > 0 &&
      Array.isArray(coords[0])
    ) {
      const last = (coords as any[])[(coords as any[]).length - 1];
      if (Array.isArray(last) && last.length === 2)
        return last as [number, number];
    }
    return null;
  };

  const removeConfirmMarker = () => {
    if (confirmMarkerRef.current) {
      confirmMarkerRef.current.remove();
      confirmMarkerRef.current = null;
    }
    if (confirmMarkerRootRef.current) {
      // Use setTimeout to defer unmounting until after current render
      setTimeout(() => {
        if (confirmMarkerRootRef.current) {
          confirmMarkerRootRef.current.unmount();
          confirmMarkerRootRef.current = null;
        }
      }, 0);
    }
  };

  const ensureConfirmMarker = (lng: number, lat: number) => {
    if (!map.current) return;
    // Helper to render/update the overlay React content to avoid stale closures
    const renderOverlay = () => {
      if (!confirmMarkerRootRef.current) return;
      confirmMarkerRootRef.current.render(
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: "flex",
            gap: 1,
            bgcolor: "#ffffff55",
            borderRadius: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            px: 1,
            py: 0.5,
            alignItems: "center",
            cursor: "default",
            userSelect: "none",
          }}
        >
          <CheckCircle
            onClick={handleConfirmClick}
            sx={{ color: "#00d4ad", cursor: "pointer", fontSize: 22 }}
          />
          {drawingMode !== DrawingMode.NOTE && (
            <Undo
              onClick={handleUndoClick}
              sx={{ color: "#FC0", cursor: "pointer", fontSize: 22 }}
            />
          )}
          <Cancel
            onClick={handleCancelClick}
            sx={{ color: "#ef4444", cursor: "pointer", fontSize: 22 }}
          />
        </Box>
      );
    };

    if (confirmMarkerRef.current) {
      confirmMarkerRef.current.setLngLat([lng, lat]);
      renderOverlay();
      return;
    }
    const el = document.createElement("div");
    const root = createRoot(el);
    confirmMarkerRootRef.current = root;
    renderOverlay();

    confirmMarkerRef.current = new mapboxgl.Marker({
      element: el,
      anchor: "bottom",
      offset: [12, -12],
    })
      .setLngLat([lng, lat])
      .addTo(map.current);
  };

  // The styleKey each layer was last built with (see Layer.styleKey). A
  // plain record: `Map` is this component's name in here.
  const appliedStyleKeys = useRef<Record<string, string>>({});

  /** The map layer drawn right above the last one bound to `sourceId`, so a
   *  rebuilt layer can go back exactly where it was. */
  const layerAfterSource = (sourceId: string): string | undefined => {
    const styleLayers = (map.current?.getStyle()?.layers ?? []) as { id: string; source?: string }[];
    let last = -1;
    styleLayers.forEach((l, i) => {
      if (l.source === sourceId) last = i;
    });
    return last >= 0 ? styleLayers[last + 1]?.id : undefined;
  };

  /** Every map layer bound to `sourceId`, including styles the layer no
   *  longer declares (a project's glow ring, absent while it initialises);
   *  Mapbox refuses to remove a source that any layer still uses. */
  const removeLayersUsingSource = (sourceId: string) => {
    const styleLayers = (map.current?.getStyle()?.layers ?? []) as { id: string; source?: string }[];
    styleLayers.forEach((l) => {
      if (l.source === sourceId && map.current?.getLayer(l.id)) map.current.removeLayer(l.id);
    });
  };

  const getLayerStyleIds = (layer: Layer) => {
    const styles = Array.isArray(layer.layerStyle)
      ? layer.layerStyle
      : [layer.layerStyle];
    return [...new Set([layer.id, ...styles.map((style) => style?.id).filter(Boolean)])];
  };

  const removeExistingLayerAndSource = (layer: Layer, sourceId: string) => {
    if (!map.current) return;

    getLayerStyleIds(layer).forEach((styleId) => {
      if (map.current?.getLayer(styleId)) {
        map.current.removeLayer(styleId);
      }
    });

    if (map.current.getSource(sourceId)) {
      map.current.removeSource(sourceId);
    }
  };

  const getExpectedSourceType = (layer: Layer) => {
    if (layer.type === "raster") {
      if (layer.source?.tiles) {
        return "raster";
      }
      if (layer.source?.canvas) {
        return "canvas";
      }
      return "image";
    }

    if (layer.type === "vector") {
      return "vector";
    }

    if (layer.source !== undefined) {
      return "geojson";
    }

    return null;
  };

  const hasCompatibleExistingSource = (layer: Layer, sourceId: string) => {
    if (!map.current) return false;

    const expectedSourceType = getExpectedSourceType(layer);
    if (!expectedSourceType) return true;

    const existingSourceType = (map.current.getStyle() as any)?.sources?.[sourceId]?.type;
    if (!existingSourceType) return true;

    return existingSourceType === expectedSourceType;
  };

  // The layer the user last picked something from. Its polygons are drawn
  // above every other layer so the thing they chose is the thing they see;
  // re-applied after every layer rebuild so a stale re-add can't bury it.
  const topLayerIdRef = useRef<string | null>(null);
  const applyTopLayer = () => {
    const topId = topLayerIdRef.current;
    if (!map.current || !topId) return;
    // Companion layers (vegetation's "-fine" detail tiles) ride along.
    const group = layers.filter((l) => l.id === topId || l.id.startsWith(`${topId}-`));
    for (const l of group) {
      const styles = Array.isArray(l.layerStyle) ? l.layerStyle : [l.layerStyle];
      for (const style of styles) {
        // Moving each in declared order keeps fill under outline under points.
        if (style?.id && map.current.getLayer(style.id)) map.current.moveLayer(style.id);
      }
    }
  };
  const bringLayerToTop = (layer: Layer | undefined) => {
    if (!layer) return;
    topLayerIdRef.current = layer.id;
    applyTopLayer();
  };

  // Function to add or update a layer
  const addOrUpdateLayer = (layer: Layer) => {
    if (!map.current) return;

    const sourceId = layer.id;
    const layerExists =
      map.current.getLayer(layer.id) || map.current.getSource(layer.id);

    // A layer whose style depends on state declares a styleKey. The update
    // path below only swaps a geojson layer's data, so once the key changes
    // (a project finishing initialisation: grey fill and "Initializing…"
    // label to be replaced) the layer is rebuilt, in the same z-order.
    const styleChanged =
      layer.styleKey !== undefined &&
      !!layerExists &&
      appliedStyleKeys.current[layer.id] !== layer.styleKey;

    if (layerExists && !styleChanged && hasCompatibleExistingSource(layer, sourceId)) {
      updateExistingLayer(layer, sourceId);
    } else {
      let beforeId: string | undefined;
      if (layerExists) {
        if (styleChanged) {
          beforeId = layerAfterSource(sourceId);
          removeLayersUsingSource(sourceId);
        }
        removeExistingLayerAndSource(layer, sourceId);
      }
      addNewLayer(layer, sourceId, beforeId);
    }
    if (layer.styleKey !== undefined) {
      appliedStyleKeys.current[layer.id] = layer.styleKey;
    }
  };

  // Confirm/cancel handlers for overlay
  const handleConfirmClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Reuse existing Enter-to-open behavior
    const enterEvent = new KeyboardEvent("keydown", { key: "Enter" });
    window.dispatchEvent(enterEvent);
  };

  const handleCancelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCoords([] as any);
    setDrawingMode(DrawingMode.NONE);
    setDrawingFeatureType(DrawingFeatureType.NONE);
  };

  const handleUndoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!coords) return;
    if (
      Array.isArray(coords) &&
      coords.length === 2 &&
      typeof (coords as any)[0] === "number"
    ) {
      setCoords([] as any);
      return;
    }
    if (Array.isArray(coords) && Array.isArray((coords as any[])[0])) {
      const next = (coords as any[]).slice(0, Math.max(0, (coords as any[]).length - 1));
      setCoords(next as any);
    }
  };

  // Function to update an existing layer
  const updateExistingLayer = (layer: Layer, sourceId: string) => {
    if (!map.current) return;

    if (layer.type === "drawing-layer") {
      removeExistingLayerAndSource(layer, sourceId);
      addNewLayer(layer, sourceId);
    } else if (layer.type === "raster") {
      if (layer.source.tiles) {
        return;
      }
      if (layer.source.canvas) {
        const canvasSource = map.current.getSource(sourceId) as (mapboxgl.CanvasSource & {
          setCoordinates?: (coordinates: [[number, number], [number, number], [number, number], [number, number]]) => void;
        }) | null;
        if (canvasSource && typeof canvasSource.setCoordinates === "function") {
          canvasSource.setCoordinates(layer.source.coordinates);
        }
        return;
      }
      const imageSource = map.current.getSource(
        sourceId
      ) as (mapboxgl.ImageSource & {
        url?: string;
        options?: { url?: string };
        coordinates?: number[][];
        updateImage?: (options: {
          url: string;
          coordinates: [[number, number], [number, number], [number, number], [number, number]];
        }) => void;
      }) | null;
      if (imageSource && typeof imageSource.updateImage === "function") {
        // updateImage re-fetches the image even when nothing changed. Layers
        // that stay permanently stale (the data-review photo slots) would
        // otherwise re-request their image on every pass — thousands of
        // fetches while an animation plays. Only push a real change.
        const currentUrl = imageSource.url ?? imageSource.options?.url;
        const sameUrl = currentUrl === layer.source.url;
        const sameCoords =
          JSON.stringify(imageSource.coordinates ?? null) ===
          JSON.stringify(layer.source.coordinates ?? null);
        if (sameUrl && sameCoords) return;
        imageSource.updateImage({
          url: layer.source.url,
          coordinates: layer.source.coordinates,
        });
      } else {
        removeExistingLayerAndSource(layer, sourceId);
        addNewLayer(layer, sourceId);
      }
    } else if (layer.type === "vector") {
      // Reload vector tiles only when the tile URL actually changes to avoid flicker
      const existingSrc = map.current.getSource(sourceId) as any;
      const oldTile = existingSrc?.tiles?.[0] ?? "";
      const newTile = layer.source?.tiles?.[0] ?? "";
      if (oldTile !== newTile) {
        removeExistingLayerAndSource(layer, sourceId);
        addNewLayer(layer, sourceId);
        return;
      }
      // Update existing vector layer properties without full teardown to minimise flicker.
      const styles = Array.isArray(layer.layerStyle)
        ? layer.layerStyle
        : [layer.layerStyle];
      styles.forEach((style) => {
        if (map.current?.getLayer(style.id)) {
          if (style.filter) {
            try {
              map.current!.setFilter(style.id, style.filter as any);
            } catch (_) {
              /* ignore */
            }
          }
          if (style.paint) {
            Object.entries(style.paint).forEach(([key, value]) => {
              try {
                // Cast key to any to satisfy TypeScript, since dynamic paint properties are controlled at runtime
              map.current!.setPaintProperty(style.id, key as any, value as any);
              } catch (_) {
                /* ignore */
              }
            });
          }
        }
      });
      // No need to recreate source/layer – early return.
      return;
    } else if (layer.source !== undefined) {
      const source = map.current.getSource(sourceId) as
        | (mapboxgl.GeoJSONSource & { setData(data: any): void })
        | { setData?: undefined }
        | null;
      if (source && typeof (source as any).setData === "function") {
        (source as mapboxgl.GeoJSONSource).setData(layer.source);
      }
    }
  };

  // Function to add a new layer
  const addNewLayer = (layer: Layer, sourceId: string, beforeIdOverride?: string) => {
    if (!map.current || !layer.source) return;

    if (layer.type === "raster") {
      if (layer.source.tiles) {
        // Mapbox validates the source spec and rejects a key whose value is
        // undefined ("bounds: array expected, undefined found"), after which
        // the source is never added and the layer silently fails. Only send
        // what the layer actually set.
        const rasterSpec: Record<string, unknown> = {
          type: "raster",
          tiles: layer.source.tiles,
          tileSize: layer.source.tileSize ?? 256,
        };
        for (const key of ["bounds", "minzoom", "maxzoom", "scheme", "attribution"] as const) {
          if (layer.source[key] !== undefined) rasterSpec[key] = layer.source[key];
        }
        map.current.addSource(sourceId, rasterSpec as any);
      } else if (layer.source.canvas) {
        map.current.addSource(sourceId, {
          type: "canvas",
          canvas: layer.source.canvas,
          coordinates: layer.source.coordinates,
          animate: layer.source.animate ?? false,
        } as any);
      } else {
        map.current.addSource(sourceId, {
          type: "image",
          url: layer.source.url,
          coordinates: layer.source.coordinates,
        });
      }
    } else if (layer.type === "vector") {
      map.current.addSource(sourceId, {
        type: "vector",
        tiles: layer.source.tiles,
        minzoom: layer.source.minzoom,
        maxzoom: layer.source.maxzoom,
        tileSize: layer.source.tileSize ?? 256,
        promoteId: layer.source.promoteId,
      } as any);
    } else {
      map.current.addSource(sourceId, {
        type: "geojson",
        data: layer.source,
      });
    }

    const styles = Array.isArray(layer.layerStyle)
      ? layer.layerStyle
      : [layer.layerStyle];
    styles.forEach((style) => {
      // Check if road-label exists before using it as a reference
      // A rebuild keeps its old position; a fresh layer goes under the
      // road labels, or on top when it asks to.
      let beforeLayerId: string | undefined =
        beforeIdOverride && map.current?.getLayer(beforeIdOverride) ? beforeIdOverride : undefined;
      if (beforeLayerId === undefined && !layer.onTop && map.current) {
        // Only use road-label as a reference if it exists and layer is not set to be on top
        try {
          const hasRoadLabel = map.current.getLayer("road-label");
          if (hasRoadLabel) {
            beforeLayerId = "road-label";
          }
        } catch (e) {
          // Layer doesn't exist, will add to the top instead
          console.debug("road-label layer not found, adding to top");
        }
      }

      // Add the layer with appropriate positioning
      map.current?.addLayer(
        {
          ...style,
          source: sourceId,
        },
        beforeLayerId
      );
    });
  };

  // Function to handle mouse move events
  const handleMouseMove = (e: mapboxgl.MapMouseEvent) => {
    if (!map.current) return;

    // Exclude raster image layers from hover (they use click-to-query instead)
    // Vector tile animation layers retain hover behaviour
    const hoverableLayers = layers.filter(
      (layer) => layer.type !== 'image'
    );

    const activeLayerIds = hoverableLayers
      .filter((layer) => map.current?.getLayer(layer.id))
      .map((layer) => layer.id);
    const interactiveIds = hoverableLayers
      .filter((layer) => layer.interactiveLayerIds)
      .flatMap((layer) => layer.interactiveLayerIds || [])
      .filter((layerId) => map.current?.getLayer(layerId));
    const allHoverableIds = [...new Set([...activeLayerIds, ...interactiveIds])];
    
    const features = map.current.queryRenderedFeatures(e.point, {
      layers: allHoverableIds,
    });

    // Layers that mirror the hover (a legend tick, say) get their own
    // topmost feature, or null so they can clear it.
    for (const layer of hoverableLayers) {
      if (!layer.onHover) continue;
      const ownIds = new Set([layer.id, ...(layer.interactiveLayerIds ?? [])]);
      layer.onHover(
        features.find((f) => ownIds.has(f.layer?.id ?? "")) ?? null,
        { x: e.point.x, y: e.point.y }
      );
    }

    if (features.length > 0) {
      const feature = features[0];
      const props: any = feature.properties || {};
      const layerObj = layers.find((l) => l.id === feature.layer?.id);
      const layerLabel = layerObj?.name ?? "";
      let label: string;
      if (props.hoverLabel) {
        label = props.hoverLabel;
      } else if (props.value !== undefined) {
        const numeric = typeof props.value === "number" ? props.value : parseFloat(props.value);
        if (isNaN(numeric)) {
          label = `${layerLabel}: ${props.value}`;
        } else {
          // Format with proper units and conversions
          let formattedValue: string;
          if (layerLabel === "Rate of Spread") {
            formattedValue = formatSpeed(numeric, unitSystem);
          } else if (layerLabel === "Fireline Intensity") {
            formattedValue = formatFirelineIntensity(numeric, unitSystem);
          } else if (layerLabel === "Flame Height") {
            formattedValue = formatLength(numeric, unitSystem);
          } else if (layerLabel === "Isochrones") {
            formattedValue = `${formatNumber(numeric)} hr`;
          } else {
            formattedValue = formatNumber(numeric);
          }
          label = `${layerLabel}: ${formattedValue}`;
        }
      } else if (props.fuelLoad !== undefined && !isNaN(parseFloat(props.fuelLoad))) {
        // Fuel-load tiles carry the load under its own name.
        label = `${layerLabel}: ${formatFuelLoad(parseFloat(props.fuelLoad), unitSystem)}`;
      } else if (typeof props.VegClass === "string" && props.VegClass) {
        // Vegetation tiles carry the class name, not a ready-made label.
        label = `${layerLabel}: ${props.VegClass}`;
      } else {
        const named = featureLabel(feature);
        label = named ? `${layerLabel}: ${named}` : "";
      }
      setHoverText(label);
      map.current.getCanvas().style.cursor = "pointer";
    } else {
      setHoverText("");
      map.current.getCanvas().style.cursor = "";
    }
  };

  // Function to handle map click events
  const handleClick = (e: mapboxgl.MapMouseEvent) => {
    if (!map.current) return;
    const isShift = isShiftHeldRef.current;
    // Close any open popovers when clicking elsewhere
    setSelectedTreatment(null);
    setSelectedAsset(null);
    setSelectedNote(null);
    setPopoverPosition(null);
    setFeaturePicker(null);

    if (!isShift) {
      setSelectedAssetFeatures([]);
      setSelectedVegetationFeatures([]);
    }

    if (
      drawingFeatureType === DrawingFeatureType.POLYGON ||
      drawingFeatureType === DrawingFeatureType.LINE ||
      drawingFeatureType === DrawingFeatureType.POINT
    ) {
      const { lng, lat } = e.lngLat;

      const isMeasureMode = drawingMode === DrawingMode.MEASURE;

      const pb = bboxFromBoundary(project?.data?.boundary);
      const isWithinBorder = pb
        ? lat <= pb.top && lat >= pb.bottom && lng >= pb.left && lng <= pb.right
        : true;

      if (!isMeasureMode && !isWithinBorder) {
        const popup = new mapboxgl.Popup({ closeButton: false })
          .setLngLat([lng, lat])
          .setHTML(
            '<div style="background-color: #FFFBE6; color: #A94442; font-size: 14px;">Your selection must be within the project area.</div>'
          )
          .addTo(map.current!);

        setTimeout(() => {
          popup.remove();
        }, 1000);

        return;
      }

      if (drawingFeatureType === DrawingFeatureType.POINT) {
        setCoords([lng, lat]);
      } else {
        setCoords([...coords, [lng, lat]]);
      }
    } else {
      // Get all layers with interactive IDs for click handling
      const interactiveLayers = layers.filter(
        (layer) =>
          layer.interactiveLayerIds && layer.interactiveLayerIds.length > 0
      );
      const allInteractiveLayerIds = interactiveLayers
        .flatMap((layer) => layer.interactiveLayerIds || [])
        .filter((layerId) => map.current?.getLayer(layerId));
      if (map.current.getLayer(URBAN_BUILDING_LAYER_ID)) {
        allInteractiveLayerIds.push(URBAN_BUILDING_LAYER_ID);
      }

      // Handle raster layer clicks first (they don't support queryRenderedFeatures)
      for (const layer of interactiveLayers) {
        if (!layer.onClick || layer.type !== 'raster') continue;
        if (!layer.active) continue;
        // Raster layers can't be queried for features, dispatch click directly
        const handled = layer.onClick(e);
        if (handled !== false) {
          return;
        }
      }

      // Handle vector/geojson layer clicks via feature queries
      let clickedFeatures: mapboxgl.MapboxGeoJSONFeature[] = [];
      if (allInteractiveLayerIds.length > 0) {
        try {
          clickedFeatures = map.current.queryRenderedFeatures(e.point, {
            layers: allInteractiveLayerIds,
          });
        } catch (err) {
          console.warn("Failed to query rendered features for interactive layers", err);
          clickedFeatures = [];
        }
      }

      const screenPoint = mapEventScreenPoint(e);
      const picks: MapPick[] = [];

      // Vegetation polygons open the unified edit modal: Shift+click toggles
      // a polygon into the multi-select set (same gesture the Assets layer
      // uses for buildings); a plain click edits that single polygon. The
      // layer has no onClick of its own, so it is offered here. Whether a
      // selection was active is read now: the non-Shift clear at the top of
      // this handler has already dismissed it, and we must not also open the
      // editor when the pick runs later from the menu.
      const vegFeature = clickedFeatures.find((f) => f.source === "vegetation");
      const vegSelectionWasActive = selectedVegCountRef.current > 0;
      if (vegFeature) {
        const vegLayer = layers.find((l) =>
          l.interactiveLayerIds?.includes(vegFeature.layer?.id ?? "")
        );
        const props = (vegFeature.properties as any) ?? {};
        const fid = vegFeature.id ?? props.__id ?? props.id;
        const vegClass = typeof props.VegClass === "string" ? props.VegClass : undefined;
        picks.push({
          key: `vegetation:${fid ?? ""}`,
          primary: vegClass ?? vegLayer?.name ?? "Vegetation",
          secondary: vegClass ? vegLayer?.name ?? "Vegetation" : undefined,
          color: typeof props.color === "string" ? props.color : undefined,
          run: () => {
            bringLayerToTop(vegLayer);
            if (isShift) {
              // Reveg-treatment polygons have no editable DB rows — keep them
              // out of the edit selection instead of silently dropping them
              // at apply time.
              if (fid != null && !isRevegVegFeature(props)) {
                setSelectedVegetationFeatures((prev) => {
                  const exists = prev.some((x) => x.id === fid);
                  if (exists) return prev.filter((x) => x.id !== fid);
                  return [...prev, { id: fid, feature: vegFeature as GeoJSON.Feature }];
                });
              }
            } else if (!vegSelectionWasActive) {
              if (isRevegVegFeature(props)) {
                const yearsRaw = Number(props.years_since_reveg);
                setVegRevegInfo({
                  vegClass: String(props.VegClass ?? ""),
                  lmax: Number.isFinite(Number(props.lmax)) ? Number(props.lmax) : undefined,
                  k: Number.isFinite(Number(props.k)) ? Number(props.k) : undefined,
                  yearsSinceReveg: Number.isFinite(yearsRaw) ? yearsRaw : undefined,
                });
              } else {
                const memberIds = parseVegMemberIds(props);
                if (memberIds.length > 0) {
                  setVegEditSinglePolygon(true);
                  setVegMultiEditIds(memberIds);
                }
              }
            }
          },
        });
      }

      // One candidate per interactive layer under the cursor. A layer gets all
      // of its own features, so one that arbitrates between its own stacked
      // polygons (fire history) keeps doing that itself.
      for (const layer of interactiveLayers) {
        if (!layer.onClick || !layer.interactiveLayerIds?.length) continue;

        const existingIds = layer.interactiveLayerIds.filter((layerId) =>
          map.current?.getLayer(layerId)
        );
        if (existingIds.length === 0) continue;

        const layerFeatures = clickedFeatures.filter(
          (f) => f.layer && existingIds.includes(f.layer.id)
        );
        if (layerFeatures.length === 0) continue;

        const first = layerFeatures[0];
        const label = featureLabel(first);
        const color = first.properties?.color;
        picks.push({
          key: `${layer.id}:${first.id ?? first.properties?.id ?? ""}`,
          primary: label ?? layer.name,
          secondary: label ? layer.name : undefined,
          color: typeof color === "string" ? color : undefined,
          run: () => {
            bringLayerToTop(layer);
            layer.onClick?.({ ...e, features: layerFeatures });
          },
        });
      }

      // Treatments, assets and notes declare no interactive ids; they come
      // from a query over the layers' primary ids. Skip layers that explicitly
      // opt out of pointer interaction by declaring an empty interactiveLayerIds
      // (e.g. the 3D Fuel overlay's invisible, full-coverage fill). Otherwise
      // such a layer's features shadow the treatments/assets/notes underneath
      // in queryRenderedFeatures, so features[0] is never a treatment/asset
      // and the click selects nothing (GH #141).
      const activeLayerIds = layers
        .filter((layer) => map.current?.getLayer(layer.id))
        .filter(
          (layer) =>
            !(
              Array.isArray(layer.interactiveLayerIds) &&
              layer.interactiveLayerIds.length === 0
            )
        )
        .map((layer) => layer.id);
      const features = map.current.queryRenderedFeatures(e.point, {
        layers: activeLayerIds,
      });

      const seenLegacy = new Set<string>();
      for (const feature of features) {
        const kind = legacyFeatureKind(feature);
        if (!kind) continue;
        const key = `${kind}:${feature.id ?? feature.properties?.id ?? ""}`;
        if (seenLegacy.has(key)) continue;
        seenLegacy.add(key);
        const props: Record<string, unknown> = feature.properties ?? {};
        const text = (k: string) => (typeof props[k] === "string" ? (props[k] as string) : "");
        picks.push({
          key,
          primary:
            kind === "treatment"
              ? text("Name") || "Treatment"
              : kind === "asset"
                ? featureLabel(feature) ?? "Asset"
                : "Note",
          secondary:
            kind === "treatment"
              ? text("Type")
              : kind === "asset"
                ? "Assets"
                : text("full_text").slice(0, 60),
          run: () => {
            bringLayerToTop(layers.find((l) => l.id === feature.source));
            selectLegacyFeature(feature, e, isShift);
          },
        });
      }

      if (picks.length > 1) {
        // Several things under the cursor: ask which one was meant.
        setSelectedPolygonId(null);
        setFeaturePicker({ anchor: screenPoint, options: picks });
        return;
      }
      if (picks.length === 1) {
        picks[0].run();
        return;
      }

      // Nothing pickable here; keep the plain selection for whatever was hit.
      setSelectedLatLng({ lat: e.lngLat.lat, lng: e.lngLat.lng });
      if (features.length > 0) {
        setSelectedPolygonId(features[0]?.properties?.id || null);
        setPopoverPosition(screenPoint);
      } else {
        setSelectedPolygonId(null);
      }
    }
  };

  // Select a treatment, asset or note found by the legacy (primary-id) query:
  // the popover anchors at the click, Shift adds an asset to the multi-select
  // instead of opening it.
  const selectLegacyFeature = (
    feature: mapboxgl.MapboxGeoJSONFeature,
    e: mapboxgl.MapMouseEvent,
    isShift: boolean
  ) => {
    setSelectedLatLng({ lat: e.lngLat.lat, lng: e.lngLat.lng });
    setSelectedPolygonId(feature?.properties?.id || null);
    setPopoverPosition(mapEventScreenPoint(e));

    if (feature.source === "treatments") {
      setSelectedTreatment(feature as unknown as Treatment);
    } else if (isAssetSource(feature.source)) {
      if (isShift) {
        setSelectedAssetFeatures((prev) => {
          const id = feature.id!;
          const exists = prev.some((x) => x.id === id);
          if (exists) {
            return prev.filter((x) => x.id !== id);
          }
          return [...prev, { id, feature: feature as GeoJSON.Feature }];
        });
      } else {
        setSelectedAsset(feature as GeoJSON.Feature);
      }
    } else if (feature.layer && feature.layer.id === "notes") {
      setSelectedNote({
        id: feature.properties?.id,
        full_text: feature.properties?.full_text,
        dateAdded: feature.properties?.dateAdded,
        photoUrl: feature.properties?.photoUrl,
        lat:
          feature.geometry?.type === "Point"
            ? feature.geometry.coordinates[1]
            : 0,
        lng:
          feature.geometry?.type === "Point"
            ? feature.geometry.coordinates[0]
            : 0,
      });
    }
  };

  // Initialize the map
  useEffect(() => {
    if (projectLoading || map.current) return;

    const projectBbox = project?.data ? bboxFromBoundary(project.data.boundary) : null;
    const initialCenter = latlng
      ? latlng
      : projectBbox
      ? {
          lng: (projectBbox.right + projectBbox.left) / 2,
          lat: (projectBbox.bottom + projectBbox.top) / 2,
        }
      : { lng: 151.2, lat: -33.85 };

    // The UI is scaled with CSS zoom on small screens (index.css --ui-zoom);
    // see utils/uiZoom for why the container must report its layout size.
    const containerEl = mapContainer.current!;
    reportLayoutSizeToMapbox(containerEl);

    map.current = new mapboxgl.Map({
      container: containerEl,
      style: "mapbox://styles/mapbox/satellite-streets-v12",
      center: initialCenter,
      zoom: zoom,
      pitch: 0,
      bearing: 0,
      antialias: true,
      preserveDrawingBuffer: true,
    });

    // Disable BoxZoom so shift+click fires the normal click event for multi-select
    map.current.boxZoom.disable();

    // Provide map instance to context
    setMap(map.current);

    map.current.on("load", () => {
      map.current?.addSource("mapbox-dem", {
        type: "raster-dem",
        url: "mapbox://mapbox.mapbox-terrain-dem-v1",
        tileSize: 512,
        maxzoom: 14,
      });
      map.current?.setTerrain({ source: "mapbox-dem", exaggeration: 1.5 });

      // Add dark mask layer outside the project boundary
      map.current?.addSource("project-mask", {
        type: "geojson",
        data: buildMaskGeoJSON(project?.data?.boundary ?? null),
      });
      map.current?.addLayer({
        id: "project-mask-layer",
        type: "fill",
        source: "project-mask",
        paint: {
          "fill-color": "#aaaaaa",
          "fill-opacity": 0.5,
        },
      });

      setMapReady(true);
    });

    return () => {
      map.current?.remove();
      map.current = null;
      setMap(null);
    };
  }, [projectLoading]);

  // Keep the canvas sized to its container. mapbox-gl only watches window
  // resizes; a pane that is dragged wider or expanded changes the container
  // without one and would otherwise leave stretched tiles behind.
  useEffect(() => {
    const el = mapContainer.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      map.current?.resize();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [mapReady]);

  useEffect(() => {
    if (!map.current) return;
    map.current.on("load", () => {
      map?.current?.loadImage("/pin.png", (error, image) => {
        if (error) throw error;
        if (image !== null && image !== undefined) {
          map.current?.addImage("pin", image);
        } else {
          console.error("Image is undefined or null");
        }
      });
    });
  }, []);

  // Add or update layers when they change
  useEffect(() => {
    if (!mapReady || !map.current) return;

    const applyLayers = () => {
      if (!map.current) return;

      layers.forEach((layer) => {
        const styles = Array.isArray(layer.layerStyle)
          ? layer.layerStyle
          : [layer.layerStyle];

        // Re-showing a vector tile layer that was hidden: while it had no
        // visible layer, its source stops loading tiles, so toggling visibility
        // back to "visible" leaves it blank until the next camera move. A clean
        // remove + re-add forces the source to fetch tiles for the current view
        // right away. Detect the hidden→visible transition from the live map.
        const firstStyle = styles[0];
        const wasHidden =
          !!firstStyle &&
          !!map.current?.getLayer(firstStyle.id) &&
          (map.current.getLayoutProperty(firstStyle.id, "visibility") ??
            "visible") === "none";
        if (layer.active && wasHidden && layer.type === "vector" && layer.source) {
          removeExistingLayerAndSource(layer, layer.id);
          addNewLayer(layer, layer.id);
          if (layer.setStale) layer.setStale(false);
          return;
        }

        if (layer.stale && layer.setStale) {
          addOrUpdateLayer(layer);
          layer.setStale(false);
        }
        styles.forEach((style) => {
          if (map?.current?.getLayer(style.id)) {
            map?.current?.setLayoutProperty(
              style.id,
              "visibility",
              layer.active ? "visible" : "none"
            );
            // Raster paint follows the declared style every render, not only
            // when the layer marks itself stale. A raster whose paint is
            // driven by user state -- an opacity slider -- changes nothing
            // else, so it would otherwise never reach the map: the stale path
            // is for source changes, and updateExistingLayer applies paint on
            // the vector branch only. setPaintProperty no-ops on an unchanged
            // value, so re-asserting each render costs nothing.
            //
            // Deliberately rasters only. Vector and geojson layers have paint
            // driven from elsewhere (the vegetation highlight below repaints
            // fill-color/opacity for selection and greying), and re-asserting
            // their declared style here would fight it.
            if (layer.type === "raster" && style.paint) {
              Object.entries(style.paint).forEach(([key, value]) => {
                try {
                  map.current!.setPaintProperty(
                    style.id,
                    key as keyof mapboxgl.RasterPaint,
                    value as never,
                  );
                } catch {
                  // A style may legitimately not accept every paint key.
                }
              });
            }
          }
        });
      });

      applyTopLayer();

      // Cleanup: remove any orphaned bbox-* layers/sources not present in
      // `layers`, and transient-* ones (overlays that come and go with the
      // list, like the listing map's new-project pin and parcel previews).
      try {
        const desiredIds = new Set(layers.map((l) => l.id));
        const style = map.current.getStyle();
        const sourceIds = Object.keys((style as any).sources || {});
        sourceIds.forEach((sourceId) => {
          const isBbox =
            sourceId === "bbox" || sourceId.startsWith("bbox-") || sourceId.startsWith("transient-");
          if (isBbox && !desiredIds.has(sourceId)) {
            // Remove associated layers (line/fill/text) if present
            const possibleLayerIds = [sourceId, `${sourceId}-text`];
            possibleLayerIds.forEach((lid) => {
              if (map.current?.getLayer(lid)) {
                map.current.removeLayer(lid);
              }
            });
            if (map.current?.getSource(sourceId)) {
              map.current.removeSource(sourceId);
            }
          }
        });
      } catch (e) {
        console.debug("bbox cleanup skipped:", e);
      }

      const activeLayers = layers.filter(
        (layer) => layer.active && layer.type == "project"
      );
      // Exclude background reference layers (other tenants' projects) from the
      // fitBounds calculation – a cross-hemisphere bbox would centre on the ocean.
      const fitBoundsLayers = activeLayers.filter(
        (layer) => !String(layer.id).startsWith("other-project-")
      );
      // A layer whose source has not arrived yet is null; bbox() read
      // `.geometry` off it and the ErrorBoundary unmounted the whole project
      // page (fisci-front-end #399, #400). Skip those and never let a bad
      // bounds computation take the map down.
      const features = fitBoundsLayers
        .map((layer) => layer.source)
        .filter((f): f is NonNullable<typeof f> => f != null);
      if (features.length > 0 && autoFit && !hasFitBounds.current) {
        try {
          // 2. Combine all features into a FeatureCollection
          const collection = featureCollection(features);

          // 3. Compute bounding box [minX, minY, maxX, maxY]
          const boundsArray = bbox(collection);
          // Take only the first 4 elements for 2D coordinates [west, south, east, north]
          const bounds2D = boundsArray.slice(0, 4) as [
            number,
            number,
            number,
            number
          ];

          map.current.fitBounds(bounds2D, {
            padding: 120,
            pitch: 0,
            bearing: 0,
          });
          hasFitBounds.current = true;
        } catch (e) {
          console.debug("fitBounds skipped:", e);
        }
      }
    };

    if (map.current.isStyleLoaded()) {
      applyLayers();
    } else {
      // Style not ready yet — apply as soon as it is, listening to both events:
      // `render` fires under continuous animation (an animating canvas source,
      // where `idle` never fires), while `idle` fires once a static map finishes
      // loading (where `render` stops and would otherwise strand the update —
      // the cause of a toggled-on layer not showing until the map is moved).
      const tryApply = () => {
        if (!map.current) return;
        if (map.current.isStyleLoaded()) {
          map.current.off("render", tryApply);
          map.current.off("idle", tryApply);
          applyLayers();
        }
      };
      map.current.on("render", tryApply);
      map.current.on("idle", tryApply);
      // Nudge one render so `tryApply` runs even on an otherwise idle map.
      map.current.triggerRepaint();
      return () => {
        map.current?.off("render", tryApply);
        map.current?.off("idle", tryApply);
      };
    }
  }, [layers, mapReady]);

  // Show/hide/update marker with drawing state
  useEffect(() => {
    // Only show for drawing assets/treatments with point/line/polygon
    const drawingFeatureActive =
      drawingFeatureType === DrawingFeatureType.POINT ||
      drawingFeatureType === DrawingFeatureType.LINE ||
      drawingFeatureType === DrawingFeatureType.POLYGON;
    const drawingModeActive =
      drawingMode === DrawingMode.ASSET ||
      drawingMode === DrawingMode.TREATMENT ||
      drawingMode === DrawingMode.NOTE ||
      drawingMode === DrawingMode.IGNITION ||
      drawingMode === DrawingMode.VEGETATION_EDIT ||
      drawingMode === DrawingMode.DATA_AREA;
    const last = getLastLngLat();
    if (
      !mapReady ||
      !map.current ||
      !drawingFeatureActive ||
      !drawingModeActive ||
      !last
    ) {
      removeConfirmMarker();
      return;
    }
    ensureConfirmMarker(last[0], last[1]);
  }, [coords, drawingFeatureType, drawingMode, mapReady]);

  // Suppress 403/404 errors for fuel-load tiles
  useEffect(() => {
    if (!mapReady || !map.current) return;

    const handler = (e: any) => {
      const err = e?.error || {};
      const url: string | undefined = err.url;
      if (!url) return;
      const isTile = url.includes("/fuel-load/") || url.endsWith(".pbf") || url.includes(".pbf?");
      if (isTile && (err.status === 403 || err.status === 404)) {
        // swallow — tiles may not exist yet for newly expanded areas
        return;
      }
    };
    map.current.on("error", handler);
    return () => {
      map.current?.off("error", handler);
    };
  }, [mapReady]);

  // Update the dark mask whenever the project boundary changes
  useEffect(() => {
    if (!mapReady || !map.current) return;
    const src = map.current.getSource("project-mask") as mapboxgl.GeoJSONSource | undefined;
    if (src) {
      src.setData(buildMaskGeoJSON(project?.data?.boundary ?? null) as GeoJSON.Feature<GeoJSON.Geometry>);
    }
  }, [mapReady, project?.data?.boundary]);

  // Apply feature-state highlighting for multi-selected assets
  useEffect(() => {
    if (!map.current || !mapReady) return;
    if (!map.current.getSource("assets")) return;

    const currentIds = new Set(selectedAssetFeatures.map((x) => x.id));
    const prev = prevHighlightedIdsRef.current;

    prev.forEach((id) => {
      if (!currentIds.has(id)) {
        try {
          map.current!.setFeatureState(
            { source: "assets", sourceLayer: "assets", id },
            { selected: false }
          );
        } catch (_) {}
      }
    });

    currentIds.forEach((id) => {
      if (!prev.has(id)) {
        try {
          map.current!.setFeatureState(
            { source: "assets", sourceLayer: "assets", id },
            { selected: true }
          );
        } catch (_) {}
      }
    });

    prevHighlightedIdsRef.current = currentIds;
  }, [selectedAssetFeatures, mapReady]);

  // Keep the count ref in sync so the click handler can read the current
  // selection size without re-attaching its listener on every change.
  useEffect(() => {
    selectedVegCountRef.current = selectedVegetationFeatures.length;
  }, [selectedVegetationFeatures]);

  // Highlight Shift-multi-selected vegetation polygons by repainting the
  // layer's fill from the selected ids. We match the real `id`/`__id` property
  // rather than Mapbox feature-state: the vegetation tiles' promoteId (`__id`)
  // is a tile-generation index, not the vegetation primary key, so feature-state
  // can't reliably target a polygon.
  //
  // The base paint comes from the layer's own declared style rather than being
  // hardcoded: while a fuel recalculation is in flight VegetationLayer declares
  // the greyed (recalcGreyed) paint, and repainting the hardcoded base here —
  // e.g. from the selection clear that runs on every plain click — would snap
  // the greyed layer back to full colour. setPaintProperty no-ops on equal
  // values, so re-running with unchanged paint costs nothing.
  const withSelection = (base: ExpressionSpecification | number, isSelected: unknown, selected: number): ExpressionSpecification => {
    if (Array.isArray(base) && base[0] === "step" && JSON.stringify(base[1]) === '["zoom"]') {
      const out: unknown[] = ["step", ["zoom"], ["case", isSelected, selected, base[2]]];
      for (let i = 3; i < base.length; i += 2) out.push(base[i], ["case", isSelected, selected, base[i + 1]]);
      return out as ExpressionSpecification;
    }
    return ["case", isSelected, selected, base] as unknown as ExpressionSpecification;
  };
  const basePaint = (layerId: string) => {
    const decl = layers.find((l) => l.id === layerId);
    const style = Array.isArray(decl?.layerStyle) ? decl?.layerStyle[0] : decl?.layerStyle;
    return {
      color: (style?.paint?.["fill-color"] ?? ["coalesce", ["get", "color"], "#cccccc"]) as ExpressionSpecification | string,
      opacity: (style?.paint?.["fill-opacity"] ?? 0.75) as ExpressionSpecification | number,
    };
  };
  const { color: vegBaseColor, opacity: vegBaseOpacity } = basePaint("vegetation");
  // From zoom 13 refined areas draw from the detailed tiles on top, which
  // would cover the highlight, so the selection is painted there too.
  const { color: fineBaseColor, opacity: fineBaseOpacity } = basePaint("vegetation-fine");
  useEffect(() => {
    if (!map.current || !mapReady) return;
    if (!map.current.getLayer("vegetation")) return;

    const ids = selectedVegetationFeatures
      .map((x) =>
        Number((x.feature.properties as any)?.id ?? (x.feature.properties as any)?.__id)
      )
      .filter((n) => Number.isFinite(n));
    const isSelected: any = [
      "in",
      ["to-number", ["coalesce", ["get", "id"], ["get", "__id"], -1]],
      ["literal", ids],
    ];
    const targets: Array<[string, ExpressionSpecification | string, ExpressionSpecification | number]> = [
      ["vegetation", vegBaseColor, vegBaseOpacity],
      ["vegetation-fine", fineBaseColor, fineBaseOpacity],
    ];

    for (const [layerId, baseColor, baseOpacity] of targets) {
      if (!map.current.getLayer(layerId)) continue;
      try {
        if (ids.length === 0) {
          map.current.setPaintProperty(layerId, "fill-color", baseColor);
          map.current.setPaintProperty(layerId, "fill-opacity", baseOpacity);
          continue;
        }
        map.current.setPaintProperty(layerId, "fill-color", [
          "case",
          isSelected,
          "#4a90e2",
          baseColor,
        ]);
        // The base opacity can be a zoom step (refined areas hand over to
        // their detailed tiles from zoom 13), and Mapbox only takes ["zoom"]
        // as the input of a top-level step: put the selection inside each
        // stop rather than wrapping the step in a case.
        map.current.setPaintProperty(layerId, "fill-opacity", withSelection(baseOpacity, isSelected, 0.9));
      } catch (_) {}
    }
  }, [selectedVegetationFeatures, mapReady, vegBaseColor, vegBaseOpacity, fineBaseColor, fineBaseOpacity]);

  // Track Shift key state for multi-select
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Shift") isShiftHeldRef.current = true;
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Shift") isShiftHeldRef.current = false;
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  // Cleanup marker on unmount
  useEffect(() => {
    return () => {
      removeConfirmMarker();
    };
  }, []);

  // Attach and detach event listeners for mousemove
  useEffect(() => {
    if (!mapReady || !map.current) return;

    map.current.on("mousemove", handleMouseMove);
    return () => {
      map.current?.off("mousemove", handleMouseMove);
    };
  }, [mapReady, map.current, layers]);

  // Attach and detach event listeners for click
  useEffect(() => {
    if (!map.current) return;

    map.current.on("click", handleClick);
    return () => {
      map.current?.off("click", handleClick);
    };
  }, [map.current, drawingFeatureType, coords, layers]);

  // Function to handle editing a treatment
  const handleEditTreatment = (treatment: Treatment) => {
    setTreatmentToEdit({
      treatmentId: treatment.id,
      name: treatment.properties.Name,
      type: treatment.properties.Type,
      residualFuel: treatment.properties.ResidualFuel,
      startDate: treatment.properties.StartDate,
      endDate: treatment.properties.EndDate,
      vegClass: treatment.properties.VegClass,
      vegDate: treatment.properties.VegDate,
      width: treatment.properties.width,
      costPerUnit: treatment.properties.CostPerUnit,
      elevatedFuelReductionPct: treatment.properties.ElevatedFuelReductionPct,
      surfaceFuelReductionPct: treatment.properties.SurfaceFuelReductionPct,
      removeSlash: treatment.properties.RemoveSlash,
      cbhRaiseM: treatment.properties.CbhRaiseM,
      cbdReductionPct: treatment.properties.CbdReductionPct,
      canopyHeightReductionPct: treatment.properties.CanopyHeightReductionPct,
      retardantProduct: treatment.properties.RetardantProduct,
      coverageFraction: treatment.properties.CoverageFraction,
    });
    setSelectedTreatment(null); // Close the popover
  };

  // Function to handle deleting a treatment
  const handleDeleteTreatment = (treatment: Treatment) => {
    const seriesSize = Number(treatment.properties.SeriesSize) || 1;
    setTreatmentToDelete({ id: treatment.id, seriesSize });
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setTreatmentToDelete(null);
    setSelectedTreatment(null);
    setPopoverPosition(null);
  };

  const deleteSingleTreatment = () => {
    if (!treatmentToDelete) return;
    deleteTreatment({ id: treatmentToDelete.id });
    closeDeleteDialog();
  };

  const deleteEntireSeries = () => {
    if (!treatmentToDelete) return;
    deleteTreatment({ id: treatmentToDelete.id, deleteSeries: true });
    closeDeleteDialog();
  };

  // Function to handle editing an asset
  const handleEditAsset = (asset: GeoJSON.Feature) => {
    if (asset.id) {
      const assetType = asset.properties?.asset_type ?? asset.properties?.assetType ?? "";
      const isLineLikeAssetType = /line|road|highway/i.test(String(assetType));
      const width =
        asset.properties?.width ?? (isLineLikeAssetType ? 40 : undefined);
      setAssetToEdit({
        assetId: asset.id,
        name: asset.properties?.name,
        value: asset.properties?.value,
        assetType,
        width,
        constructionTypeId: asset.properties?.construction_type_id ?? null,
        extraConstructionSettingIds: asIdArray(
          asset.properties?.extra_construction_setting_ids
        ),
      });
      setSelectedAsset(null); // Close the popover
    }
  };

  // Function to handle deleting an asset
  const handleDeleteAsset = (assetId: string | number) => {
    deleteAsset(assetId);
    setSelectedAsset(null); // Close the popover
  };

  const computeCommonAssetData = (features: GeoJSON.Feature[]): Partial<AssetFormData> => {
    if (features.length === 0) return {};
    const allProps = features.map((f) => f.properties ?? {});
    const first = allProps[0];
    const allSame = (getter: (p: any) => any) =>
      allProps.every((p) => getter(p) === getter(first));
    const firstExtras = asIdArray(first.extra_construction_setting_ids);
    const allExtrasMatch = allProps.every((p) => {
      const a = asIdArray(p.extra_construction_setting_ids);
      if (a.length !== firstExtras.length) return false;
      const sortedA = [...a].sort();
      const sortedB = [...firstExtras].sort();
      return sortedA.every((v, i) => v === sortedB[i]);
    });
    return {
      assetType: allSame((p) => p.asset_type ?? p.assetType)
        ? (first.asset_type ?? first.assetType ?? "")
        : "",
      value: allSame((p) => p.value) ? (first.value ?? 0) : 0,
      constructionTypeId: allSame((p) => p.construction_type_id)
        ? (first.construction_type_id ?? null)
        : null,
      extraConstructionSettingIds: allExtrasMatch ? firstExtras : [],
      width: allSame((p) => p.width) ? first.width : undefined,
    };
  };

  const handleEditSelectedAssets = () => {
    const features = selectedAssetFeatures.map((x) => x.feature);
    const ids = selectedAssetFeatures.map((x) => x.id).filter((id) => id != null);
    const commonData = computeCommonAssetData(features);
    setMultiEditData({ ids, commonData });
    setSelectedAssetFeatures([]);
  };

  const handleEditSelectedVegetation = () => {
    // A rendered polygon can be a dissolve of several Vegetation rows; its
    // "ids" property carries every member PK, so the edit targets all of them
    // (deduplicated — clipping can repeat a polygon across tiles).
    const ids = Array.from(
      new Set(
        selectedVegetationFeatures.flatMap((x) =>
          parseVegMemberIds(x.feature.properties as any)
        )
      )
    );
    if (ids.length === 0) return;
    setVegEditSinglePolygon(false);
    setVegMultiEditIds(ids);
    setSelectedVegetationFeatures([]);
  };

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <style>
        {`
          .mapboxgl-popup-content {
            background: #FFFBE6;
            padding: 12px 16px;
            border-radius: 8px;
            font-size: 14px;
            line-height: 1.4;
            border: none;
          }

          .mapboxgl-popup-tip {
            border-top-color: #FFFBE6 !important;
            margin-top: -1px;
          }
        `}
      </style>
      <div
        ref={mapContainer}
        className="map-container"
        style={{
          width: "100%",
          height: "calc(var(--vh100) - var(--topbar-h) - var(--strategies-h) - 16px)",
          position: "relative",
          ...style,
        }}
      >
        {/* Search bar overlay */}
        {searchable && (
          <Box
            sx={{
              position: "absolute",
              top: 10,
              left: 10,
              width: 300,
              bgcolor: "background.paper",
              borderRadius: "4px",
              zIndex: 1000,
            }}
          >
            <div ref={geocoderContainerRef} />
          </Box>
        )}

        <CompassButton />

        {drawingMode === DrawingMode.MEASURE && measureDistanceLabel && (
          <Box
            sx={{
              position: "absolute",
              bottom: 10,
              left: 10,
              bgcolor: "rgba(33, 43, 55, 0.92)",
              color: "white",
              borderRadius: "10px",
              px: 1.5,
              py: 1,
              zIndex: 1100,
              minWidth: 260,
              border: "1px solid rgba(148, 163, 184, 0.25)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {measureDistanceLabel}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Box
                component="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMeasureUndo();
                }}
                style={{
                  border: "1px solid rgba(148, 163, 184, 0.35)",
                  borderRadius: 8,
                  padding: "6px 10px",
                  background: "transparent",
                  color: "white",
                  cursor: "pointer",
                  fontSize: 12,
                }}
              >
                Undo
              </Box>
              <Box
                component="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMeasureClear();
                }}
                style={{
                  border: "1px solid rgba(148, 163, 184, 0.35)",
                  borderRadius: 8,
                  padding: "6px 10px",
                  background: "transparent",
                  color: "white",
                  cursor: "pointer",
                  fontSize: 12,
                }}
              >
                Clear
              </Box>
            </Box>
          </Box>
        )}

        {selectedAssetFeatures.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              bottom: 10,
              left: "50%",
              transform: "translateX(-50%)",
              bgcolor: "rgba(33, 43, 55, 0.92)",
              color: "white",
              borderRadius: "10px",
              px: 1.5,
              py: 1,
              zIndex: 1000,
              border: "1px solid rgba(148, 163, 184, 0.25)",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedAssetFeatures.length} asset{selectedAssetFeatures.length !== 1 ? "s" : ""} selected
              {selectedAssetFeatures.length === 1 && (
                <span style={{ fontWeight: 400, opacity: 0.7, fontSize: "0.85em", marginLeft: 6 }}>
                  · Shift+click to add more
                </span>
              )}
            </Typography>
            <Button
              size="small"
              variant="contained"
              onClick={handleEditSelectedAssets}
              sx={{ bgcolor: "#00d4ad", "&:hover": { bgcolor: "#00b89a" }, minWidth: 60 }}
            >
              Edit
            </Button>
            <Box
              component="button"
              onClick={() => setSelectedAssetFeatures([])}
              style={{
                border: "1px solid rgba(148, 163, 184, 0.35)",
                borderRadius: 8,
                padding: "6px 10px",
                background: "transparent",
                color: "white",
                cursor: "pointer",
                fontSize: 12,
              }}
            >
              Clear
            </Box>
          </Box>
        )}

        {selectedVegetationFeatures.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              // Stack above the assets bar if both are active.
              bottom: selectedAssetFeatures.length > 0 ? 70 : 10,
              left: "50%",
              transform: "translateX(-50%)",
              bgcolor: "rgba(33, 43, 55, 0.92)",
              color: "white",
              borderRadius: "10px",
              px: 1.5,
              py: 1,
              zIndex: 1000,
              border: "1px solid rgba(148, 163, 184, 0.25)",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedVegetationFeatures.length} vegetation polygon
              {selectedVegetationFeatures.length !== 1 ? "s" : ""} selected
              {selectedVegetationFeatures.length === 1 && (
                <span style={{ fontWeight: 400, opacity: 0.7, fontSize: "0.85em", marginLeft: 6 }}>
                  · Shift+click to add more
                </span>
              )}
            </Typography>
            <Button
              size="small"
              variant="contained"
              onClick={handleEditSelectedVegetation}
              sx={{ bgcolor: "#00d4ad", "&:hover": { bgcolor: "#00b89a" }, minWidth: 60 }}
            >
              Edit
            </Button>
            <Box
              component="button"
              onClick={() => setSelectedVegetationFeatures([])}
              style={{
                border: "1px solid rgba(148, 163, 184, 0.35)",
                borderRadius: 8,
                padding: "6px 10px",
                background: "transparent",
                color: "white",
                cursor: "pointer",
                fontSize: 12,
              }}
            >
              Clear
            </Box>
          </Box>
        )}
      </div>

      {/* Render any custom popovers from layers */}
      {layers.map(
        (layer) =>
          layer.popover &&
          React.cloneElement(layer.popover as React.ReactElement, {
            key: `${layer.id}-popover`,
          })
      )}

      {/* Render any custom components from layers */}
      {layers.map(
        (layer) =>
          layer.customComponent && (
            <React.Fragment key={`${layer.id}-custom`}>
              {layer.customComponent}
            </React.Fragment>
          )
      )}

      <MapFeaturePickerMenu
        anchorPosition={featurePicker?.anchor ?? null}
        options={featurePicker?.options ?? []}
        onSelect={(index) => {
          const pick = featurePicker?.options[index];
          setFeaturePicker(null);
          pick?.run();
        }}
        onClose={() => setFeaturePicker(null)}
      />

      {/* Treatment Popover */}
      <TreatmentPopover
        treatment={selectedTreatment}
        anchorPosition={popoverPosition}
        onClose={() => {
          setSelectedTreatment(null);
          setPopoverPosition(null);
        }}
        onEdit={handleEditTreatment}
        onDelete={handleDeleteTreatment}
      />

      {/* Asset Popover */}
      <AssetPopover
        asset={selectedAsset}
        anchorPosition={popoverPosition}
        onClose={() => {
          setSelectedAsset(null);
          setPopoverPosition(null);
        }}
        onEdit={handleEditAsset}
        onDelete={handleDeleteAsset}
      />

      {/* Note Popover */}
      <NotePopover
        note={selectedNote}
        anchorPosition={popoverPosition}
        onClose={() => {
          setSelectedNote(null);
          setPopoverPosition(null);
        }}
        onDelete={(noteId) => {
          deleteNote(noteId);
          setSelectedNote(null);
          setPopoverPosition(null);
        }}
      />

      {/* Treatment Edit Modal */}
      {treatmentToEdit && (
        <AddEditTreatmentModal
          mode="edit"
          initialData={treatmentToEdit}
          onClose={() => {
            setTreatmentToEdit(null);
          }}
        />
      )}

      {/* Asset Edit Modal */}
      {assetToEdit && (
        <AddEditAssetModal
          mode="edit"
          initialData={assetToEdit}
          onClose={() => {
            setAssetToEdit(null);
          }}
        />
      )}

      {/* Asset Multi-Edit Modal */}
      {multiEditData && (
        <AddEditAssetModal
          mode="edit"
          initialData={multiEditData.commonData}
          multiEditIds={multiEditData.ids}
          onClose={() => {
            setMultiEditData(null);
          }}
        />
      )}

      {/* Vegetation Multi-Edit Modal (also shows reveg polygons read-only) */}
      {((vegMultiEditIds && vegMultiEditIds.length > 0) || vegRevegInfo) && (
        <VegetationMultiEditModal
          ids={vegMultiEditIds ?? []}
          singlePolygon={vegEditSinglePolygon}
          revegInfo={vegRevegInfo}
          onClose={() => {
            setVegMultiEditIds(null);
            setVegRevegInfo(null);
          }}
        />
      )}

      {treatmentToDelete && (
        <DeleteTreatmentDialog
          open={deleteDialogOpen}
          onClose={closeDeleteDialog}
          onDeleteSingle={deleteSingleTreatment}
          onDeleteSeries={deleteEntireSeries}
          seriesSize={treatmentToDelete.seriesSize}
        />
      )}

      <Box
        sx={{
          position: "relative",
          bottom: 0,
          left: 0,
          right: 0,
          pl: 2,
          pr: 2,
          color: "white",
          backgroundColor: "#212b37ff",
          display: "flex",
          justifyContent: "space-between",
          // Pinned rather than left to the line-height, because the map legends
          // offset themselves by exactly this to sit clear of the bar.
          height: MAP_STATUS_BAR_HEIGHT,
          alignItems: "center",
        }}
      >
        <Typography variant="body2">{hoverText}</Typography>
        <Typography variant="body2">
          &copy; {new Date().getFullYear()} FiSci
        </Typography>
      </Box>
    </div>
  );
};

export default Map;
